/* ============================================================
   Desk Haus — Smooth Scroll + Reveal System (v4 — foolproof)
   Drop this file in: assets/dh-smooth-scroll.js
   Loaded via: {{ 'dh-smooth-scroll.js' | asset_url | script_tag }}

   DESIGN PRINCIPLES
   -----------------
   1. The browser owns the scroll position. Always.
   2. Lenis only smooths mouse WHEEL input. Nothing else is touched.
   3. If any other input moves the page (scrollbar, keyboard, hash
      anchor, touch, click, focus, resize, another script) — Lenis
      IMMEDIATELY abandons its target. It never fights.
   4. Wheel events over any inner scrollable container (dropdowns,
      modals, drawers, mega menus) are auto-detected and passed
      through to the browser natively.
   5. If this script errors, throws, or otherwise fails to load —
      the user still gets 100% normal native scroll. Scrolling is
      never dependent on Lenis being alive.

   WHAT THIS FIXES vs v3.2
   -----------------------
   - Scrollbar drag snap-back: eliminated. Lenis never writes to
     window.scrollY except in tiny relative deltas driven purely by
     wheel input, and it stops the instant it sees any external
     scroll movement.
   - Dropdown-blocks-scroll bug: eliminated. Lenis's `limit` is
     recomputed on every wheel tick from the live scrollHeight, so
     expanding dropdowns/mega menus never cause stuck-at-old-bottom.
     Additionally, wheel over an inner scrollable is passed through.
   - Race conditions: eliminated. There is no self-scroll flag. There
     is no comparison of expected-vs-actual scrollY. Lenis just reads
     the current scrollY at wheel time, adds delta, and walks toward
     it — cooperatively, not exclusively.

   v4.1
   ----
   - The file had been cut off mid-line, so the browser rejected it
     and none of it ever ran. Finished it.
   - Stands aside when another script already took the wheel
     (defaultPrevented, or [data-lenis-prevent] on <html> while the
     story band drives), for Ctrl/Cmd/Shift/Alt + wheel (zoom,
     trackpad pinch, sideways scroll) and for sideways gestures.
   - Chrome/Safari trackpads (wheelDeltaY === -3 × deltaY) pass
     through to native, so fast flicks aren't half-smoothed.
   - Steps use behavior 'instant', so a section's
     `html { scroll-behavior: smooth }` can't lag every frame.
   - If a step ever throws, the wheel goes back to native. If animation
     frames stall, what's pending is applied at once and that wheel
     scrolls natively.
   - Theme settings → Animations → "Smooth mouse-wheel scrolling"
     turns the smoothing off. Reveal still runs.
   ============================================================ */
(function () {
  'use strict';

  try {
    // ---------- Respect reduced motion & touch ----------
    const prefersReducedMotion =
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const isTouch =
      window.matchMedia &&
      window.matchMedia('(hover: none)').matches &&
      window.matchMedia('(pointer: coarse)').matches;

    // Theme settings → Animations → "Smooth mouse-wheel scrolling"
    // (snippets/dh-smooth-scroll.liquid passes it in).
    const script = document.currentScript;
    const wheelSetting = script ? script.getAttribute('data-smooth-wheel') : null;

    // If we're not going to smooth-scroll, still run reveal.
    const smoothEnabled = !prefersReducedMotion && !isTouch && wheelSetting !== 'false';

    // ---------- Auto-detect overlays & inner scrollables ----------
    // Any wheel event whose target sits inside one of these gets
    // passed through to native scroll. Add new drawer/modal
    // selectors here as the theme grows.
    const PASSTHROUGH_SELECTOR = [
      '[data-lenis-prevent]',
      '[aria-modal="true"]',
      '[role="dialog"]',
      '[role="menu"]',
      '[role="listbox"]',
      '.modal',
      '.modal__content',
      '.drawer',
      '.cart-drawer',
      '.search-modal',
      '.search-modal__content-bottom',
      '.mega-menu',
      '.mega-menu__content',
      '.header__submenu',
      '.header__inline-menu',
      '.menu-drawer',
      '.predictive-search',
      '.disclosure__list',
      '.select__select-output'
    ].join(', ');

    // Detect whether a specific ancestor of the wheel target can
    // actually scroll in the direction of the wheel. If yes, we pass
    // through to native. This is the "belt" behind the selector list's
    // "suspenders" — it catches ad-hoc scrollables we haven't listed.
    const hasScrollableAncestor = (target, deltaY) => {
      if (!target || target.nodeType !== 1) return false;
      let node = target;
      while (node && node !== document.body && node !== document.documentElement) {
        const style = getComputedStyle(node);
        const oy = style.overflowY;
        const scrollable =
          (oy === 'auto' || oy === 'scroll' || oy === 'overlay') &&
          node.scrollHeight > node.clientHeight + 1;
        if (scrollable) {
          const goingDown = deltaY > 0;
          const atTop = node.scrollTop <= 0;
          const atBottom =
            node.scrollTop + node.clientHeight >= node.scrollHeight - 1;
          if ((goingDown && !atBottom) || (!goingDown && !atTop)) {
            return true;
          }
        }
        node = node.parentNode;
      }
      return false;
    };

    // ---------- The smoother ----------
    // Owns nothing about the scroll position except a *pending delta*.
    // Each rAF tick, walks a fraction of that pending delta toward zero
    // by calling scrollBy — a relative operation that doesn't fight
    // any concurrent scroll movement.
    const state = {
      pending: 0,     // pixels remaining to smoothly scroll
      lastAppliedTime: 0,
      lastTickTime: 0, // when the animation last got a frame
      rafId: null,
      running: false
    };

    const LERP = 0.18;              // per-frame fraction of pending to apply
    const WHEEL_MULTIPLIER = 1;     // 1 = match native distance
    const MIN_STEP = 0.5;           // stop when remainder is under this
    const IDLE_TIMEOUT_MS = 250;    // if no wheel for this long, stop rAF

    // Set if a step ever throws: the wheel then goes straight back to
    // native scroll (principle 5).
    let broken = false;

    // 'instant' so a section's `html { scroll-behavior: smooth }` doesn't
    // turn every step into its own animation. Browsers that reject the
    // option get the plain form.
    let instantOk = true;
    const scrollStep = (dy) => {
      if (instantOk) {
        try {
          window.scrollBy({ top: dy, behavior: 'instant' });
          return;
        } catch (err) {
          instantOk = false;
        }
      }
      window.scrollBy(0, dy);
    };

    const stop = () => {
      state.pending = 0;
      state.running = false;
      if (state.rafId) {
        cancelAnimationFrame(state.rafId);
        state.rafId = null;
      }
    };

    const tick = (time) => {
      if (!state.running) return;
      state.lastTickTime = time;

      // If the user hasn't wheeled in a while and we've settled, stop.
      if (Math.abs(state.pending) < MIN_STEP) {
        state.pending = 0;
        state.running = false;
        state.rafId = null;
        return;
      }
      if (time - state.lastAppliedTime > IDLE_TIMEOUT_MS &&
          Math.abs(state.pending) < 1) {
        stop();
        return;
      }

      const step = state.pending * LERP;
      state.pending -= step;

      // Relative scroll. Never fights an external absolute scroll.
      // If the user simultaneously drags the scrollbar, presses End,
      // or opens a dropdown that resizes the page, scrollBy applies
      // AFTER whatever they did — cooperatively.
      try {
        scrollStep(step);
      } catch (err) {
        broken = true;
        stop();
        return;
      }

      state.rafId = requestAnimationFrame(tick);
    };

    // Any non-wheel scroll input abandons our pending animation.
    // We only need to listen for events that CAUSE scroll and are
    // NOT wheel. (We can't listen to `scroll` itself, because our own
    // scrollBy fires it.)
    const cancelHandlers = ['keydown', 'mousedown', 'touchstart', 'pointerdown'];
    const cancelOnInput = (e) => {
      // Middle-mouse-button autoscroll: leave alone.
      if (e.type === 'mousedown' && e.button === 1) { stop(); return; }
      // Any keyboard scroll key.
      if (e.type === 'keydown') {
        const k = e.key;
        if (k === 'ArrowUp' || k === 'ArrowDown' ||
            k === 'PageUp' || k === 'PageDown' ||
            k === 'Home' || k === 'End' || k === ' ' ||
            k === 'Spacebar') {
          stop();
        }
        return;
      }
      // Any pointer/touch start: user is about to drag scrollbar,
      // tap a link, or scroll by touch. Abandon pending.
      stop();
    };

    const onWheel = (e) => {
      // Not enabled (or broken)? Do nothing — native scroll takes over.
      if (!smoothEnabled || broken) return;

      // Another script already took this wheel (a swatch carousel, an
      // inner list). Element listeners run before this window one.
      if (e.defaultPrevented) return;

      // Ctrl/Cmd + wheel is browser zoom (and trackpad pinch); Shift +
      // wheel scrolls sideways. Leave every modified wheel to the browser.
      if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;

      // Sideways gestures aren't ours either. (Reading the deltas before
      // deltaMode also keeps Firefox reporting pixels.)
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;

      const target = e.target;

      // Pass through for overlays and drawers.
      if (target && target.closest && target.closest(PASSTHROUGH_SELECTOR)) {
        return;
      }

      // Pass through if any ancestor can actually scroll in this
      // direction — dropdowns, code blocks, inner lists, etc.
      if (hasScrollableAncestor(target, e.deltaY)) {
        return;
      }

      // If the browser is doing pixel-precise trackpad scrolling
      // (many small events with fractional deltas), let it through
      // to native — smoothing tiny native-smooth deltas adds jank.
      // Chrome and Safari also mark trackpad events with
      // wheelDeltaY === -3 × deltaY, which catches fast flicks too.
      if (e.deltaMode === 0 &&
          (Math.abs(e.deltaY) < 10 || (e.wheelDeltaY && e.wheelDeltaY === -3 * e.deltaY))) {
        return;
      }

      // Animation frames stalled (a frozen or throttled page)? Don't hold
      // the wheel hostage: apply what's pending now and let this wheel
      // scroll natively. The next one starts fresh.
      if (state.running && performance.now() - state.lastTickTime > 300) {
        scrollStep(state.pending);
        stop();
        return;
      }

      // Everything else: smooth it. Line and page deltas (Firefox,
      // some mice) become pixels.
      const unit = e.deltaMode === 1 ? 40 : e.deltaMode === 2 ? window.innerHeight : 1;
      e.preventDefault();
      state.pending += e.deltaY * unit * WHEEL_MULTIPLIER;
      state.lastAppliedTime = performance.now();

      if (!state.running) {
        state.running = true;
        state.lastTickTime = performance.now();
        state.rafId = requestAnimationFrame(tick);
      }
    };

    if (smoothEnabled) {
      window.addEventListener('wheel', onWheel, { passive: false });
      cancelHandlers.forEach((evt) => {
        window.addEventListener(evt, cancelOnInput, { passive: true, capture: true });
      });

      // Expose a tiny debug/API surface (nothing external depends on it).
      window.dhSmoothScroll = {
        version: '4.1',
        enabled: true,
        stop,
        _state: state
      };
    } else {
      window.dhSmoothScroll = { version: '4.1', enabled: false };
    }

    // ---------- Reveal-on-scroll (unchanged from v2/v3) ----------
    const initReveal = () => {
      const revealEls = document.querySelectorAll('[data-reveal], [data-reveal-stagger]');
      if (!revealEls.length) return;

      document.querySelectorAll('[data-reveal-stagger]').forEach((parent) => {
        const children = parent.children;
        const baseDelay = parseInt(parent.dataset.revealStagger, 10) || 80;
        for (let i = 0; i < children.length; i++) {
          children[i].classList.add('dh-reveal-child');
          children[i].style.transitionDelay = (i * baseDelay) + 'ms';
        }
      });

      if (prefersReducedMotion) {
        revealEls.forEach((el) => el.classList.add('dh-in-view'));
        document.querySelectorAll('.dh-reveal-child').forEach((el) =>
          el.classList.add('dh-in-view')
        );
        return;
      }

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('dh-in-view');
              entry.target.querySelectorAll('.dh-reveal-child').forEach((c) =>
                c.classList.add('dh-in-view')
              );
              observer.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.15, rootMargin: '0px 0px -8% 0px' }
      );

      revealEls.forEach((el) => observer.observe(el));
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initReveal);
    } else {
      initReveal();
    }
    document.addEventListener('shopify:section:load', initReveal);

  } catch (err) {
    // Principle 5: if setup fails, the page keeps plain native scroll.
    if (window.console && console.warn) console.warn('[dh-smooth-scroll]', err);
  }
})();
